// File: src/pages/RegisterPage.jsx
// Description: Modern, mobile-friendly registration page with enhanced UX and visual appeal.

import React, { useState, useContext } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Link as MuiLink, 
  Card, 
  CardContent,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { BASE_URL } from '../config';
import { createAuthenticatedAPI } from '../utils/apiClient';

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const RegisterPage = () => {
  const { login, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const steps = ['Account Details', 'Verification', 'Welcome!'];

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: 'default' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    
    if (score <= 2) return { score, label: 'Weak', color: 'error' };
    if (score <= 3) return { score, label: 'Fair', color: 'warning' };
    if (score <= 4) return { score, label: 'Good', color: 'info' };
    return { score, label: 'Strong', color: 'success' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear validation errors when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setError(null);
    setIsLoading(true);
    setActiveStep(1);

    try {
      // 1) REGISTER USER: POST /auth/register
      const registerResponse = await fetch(`${BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password
        }),
      });

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json();
        throw new Error(errorData.detail || 'Registration failed.');
      }

      // Expect { access_token: '...', token_type: 'bearer' }
      const tokenData = await registerResponse.json();
      const token = tokenData.access_token;
      if (!token) {
        throw new Error('No access token received from the register endpoint');
      }

      // 2) FETCH USER PROFILE: GET /users/me with the token
      const userResponse = await fetch(`${BASE_URL}/users/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!userResponse.ok) {
        const userError = await userResponse.json();
        throw new Error(userError.detail || 'Could not fetch user data.');
      }

      // userData might have fields: { id, name, email, ... }
      const userData = await userResponse.json();

      // 3) STORE IN CONTEXT (spread all returned fields)
      login({
        ...userData,
        token: token,
      });

      // 4) If there is a pending saved profile, save it now
      try {
        const pending = localStorage.getItem('pendingSavedProfile');
        if (pending) {
          const pendingProfile = JSON.parse(pending);
          
          // Create API client with user data including token
          const userDataWithToken = { token, ...userData };
          const api = createAuthenticatedAPI({ user: userDataWithToken, logout, navigate });
          
          await api.post('/users/save-profile', pendingProfile);
          
          // Clear the pending saved profile and notify
          localStorage.removeItem('pendingSavedProfile');
          setShowSuccessSnackbar(true);
        }
      } catch (e) {
        console.warn('Pending saved profile could not be processed after registration:', e);
      }

      // 5) Navigate to dashboard
      setActiveStep(2);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (err) {
      setError(err.message);
      setActiveStep(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialRegister = (provider) => {
    // TODO: Implement social registration
    console.log(`Registering with ${provider}`);
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Account Details
  return (
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Name Field */}
              <Grid item xs={12}>
        <TextField
                  fullWidth
                  name="name"
          label="Full Name"
          value={formData.name}
          onChange={handleChange}
                  required
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>

              {/* Email Field */}
              <Grid item xs={12}>
        <TextField
                  fullWidth
                  name="email"
                  label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleChange}
                  required
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>

              {/* Password Field */}
              <Grid item xs={12}>
        <TextField
                  fullWidth
                  name="password"
          label="Password"
                  type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
                  required
                  error={!!validationErrors.password}
                  helperText={validationErrors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Password strength:
                    </Typography>
                    <Chip
                      label={passwordStrength.label}
                      size="small"
                      color={passwordStrength.color}
                      variant="outlined"
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {[1, 2, 3, 4, 5].map((level) => (
                        <Box
                          key={level}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: level <= passwordStrength.score 
                              ? theme.palette[passwordStrength.color].main 
                              : theme.palette.grey[300]
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Confirm Password Field */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  error={!!validationErrors.confirmPassword}
                  helperText={validationErrors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="gradient"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    mt: 2
                  }}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Verification
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Creating Your Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we set up your Touch Connect profile...
            </Typography>
          </Box>
        );

      case 2: // Welcome
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80, 
                color: theme.palette.success.main, 
                mb: 3 
              }} 
            />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Welcome to Touch Connect! 🎉
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your account has been created successfully. Redirecting to dashboard...
            </Typography>
            <CircularProgress size={24} />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0
              }}
            >
              Join Touch Connect
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary">
            Create your digital business card in minutes
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent>
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{ 
                display: { xs: 'none', md: 'flex' },
                '& .MuiStepLabel-root .Mui-completed': {
                  color: theme.palette.success.main,
                },
                '& .MuiStepLabel-root .Mui-active': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Mobile Step Indicator */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Step {activeStep + 1} of {steps.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {steps[activeStep]}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Registration Form Card */}
        <Card sx={{
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {/* Error Alert */}
        {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
              </Alert>
            )}

            {/* Step Content */}
            {renderStepContent(activeStep)}

            {/* Social Registration (only show on first step) */}
            {activeStep === 0 && (
              <>
                <Divider sx={{ my: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    or continue with
          </Typography>
                </Divider>

                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GoogleIcon />}
                    onClick={() => handleSocialRegister('Google')}
                    sx={{
                      borderRadius: 2,
                      borderWidth: '2px',
                      py: 1.5,
                      '&:hover': {
                        borderColor: theme.palette.error.main,
                        color: theme.palette.error.main
                      }
                    }}
                  >
                    Continue with Google
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LinkedInIcon />}
                    onClick={() => handleSocialRegister('LinkedIn')}
                    sx={{
                      borderRadius: 2,
                      borderWidth: '2px',
                      py: 1.5,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    Continue with LinkedIn
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GitHubIcon />}
                    onClick={() => handleSocialRegister('GitHub')}
                    sx={{
                      borderRadius: 2,
                      borderWidth: '2px',
                      py: 1.5,
                      '&:hover': {
                        borderColor: theme.palette.text.primary,
                        color: theme.palette.text.primary
                      }
                    }}
                  >
                    Continue with GitHub
                  </Button>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sign In Link */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Already have an account?
          </Typography>
          <MuiLink
            component={Link}
            to="/login"
            variant="body2"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Sign in here
          </MuiLink>
        </Box>

        {/* Trust Indicators */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Chip 
              label="🔒 Secure & Private" 
              size="small" 
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
            <Chip 
              label="⚡ Setup in 2 Minutes" 
              size="small" 
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
            <Chip 
              label="💯 Free Forever" 
              size="small" 
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          </Stack>
      </Box>

        {/* Success Snackbar */}
        <Snackbar
          open={showSuccessSnackbar}
          autoHideDuration={4000}
          onClose={() => setShowSuccessSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowSuccessSnackbar(false)}
            severity="success"
            variant="filled"
            sx={{ 
              width: '100%',
              borderRadius: 2
            }}
          >
            Profile has been added to your network! 🎉
          </Alert>
        </Snackbar>
    </Container>
    </Box>
  );
};

export default RegisterPage;
